import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Phone, Target, CheckCircle2, Clock, Calendar, ShieldAlert, Pencil, Save, X, Wallet } from 'lucide-react';
import { format } from 'date-fns';
import { getApiUrl } from '@/lib/utils';
import { AddToHomeScreen } from '@/components/AddToHomeScreen';

const API_URL = getApiUrl();

const CRICKET_ROLES = [
  'Batsman',
  'Bowler',
  'Keeper',
  'Batting Allrounder',
  'Bowling Allrounder'
];

function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    cricket_role: '',
    nickname: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, paymentsRes, walletRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/payments/user`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/users/wallet`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);

      setUser(userRes.data);
      setEditForm({
        name: userRes.data.name || '',
        phone: userRes.data.phone || '',
        cricket_role: userRes.data.cricket_role || 'Batsman',
        nickname: userRes.data.nickname || ''
      });
      setPayments(paymentsRes.data);
      setBalance(walletRes.data.wallet_balance || 0);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/auth/profile`,
        {
          name: editForm.name.trim(),
          phone: editForm.phone.trim(),
          cricket_role: editForm.cricket_role,
          nickname: editForm.nickname.trim() || null
        },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      setUser(res.data);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditForm({
      name: user?.name || '',
      phone: user?.phone || '',
      cricket_role: user?.cricket_role || 'Batsman',
      nickname: user?.nickname || ''
    });
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const pendingPayments = payments.filter(p => p.status === 'pending');
  const completedPayments = payments.filter(p => p.status === 'completed');
  const totalPaid = completedPayments.reduce((sum, p) => sum + p.amount, 0);
  const totalDue = pendingPayments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="icon"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-[#0F172A]">My Profile</h1>
          </div>
          {!editing ? (
            <Button
              onClick={() => setEditing(true)}
              variant="outline"
              size="sm"
              className="text-[#E63946] border-[#E63946] hover:bg-[#E63946] hover:text-white"
              data-testid="edit-profile-btn"
            >
              <Pencil className="w-4 h-4 mr-1" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                onClick={handleCancel}
                variant="outline"
                size="sm"
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-[#E63946] flex items-center justify-center text-white text-2xl font-bold">
              {(user?.nickname || user?.name)?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#0F172A]">{user?.name}</h2>
              {user?.is_admin && (
                <span className="inline-block px-3 py-1 bg-[#E63946] text-white text-xs font-bold rounded-full mt-1">
                  ADMIN
                </span>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Name - editable */}
            <EditableField
              icon={<User />}
              label="Full Name"
              value={user?.name}
              editing={editing}
              editValue={editForm.name}
              onChange={(val) => setEditForm({ ...editForm, name: val })}
              placeholder="Enter your name"
              testId="name"
            />

            {/* Nickname - editable */}
            <EditableField
              icon={<User />}
              label="Nickname (Display Name)"
              value={user?.nickname || <span className="text-slate-400 italic">Not set</span>}
              editing={editing}
              editValue={editForm.nickname}
              onChange={(val) => setEditForm({ ...editForm, nickname: val })}
              placeholder="Enter nickname for team sheets"
              testId="nickname"
              hint="This name will be used on team sheets instead of your full name"
            />

            {/* Email - NOT editable */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="text-slate-600"><Mail className="w-5 h-5" /></div>
              <div className="flex-1">
                <p className="text-xs text-slate-500">Email (cannot be changed)</p>
                <p className="font-semibold text-[#0F172A]">{user?.email}</p>
              </div>
            </div>

            {/* Phone - editable */}
            <EditableField
              icon={<Phone />}
              label="Phone"
              value={user?.phone || <span className="text-slate-400 italic">Not set</span>}
              editing={editing}
              editValue={editForm.phone}
              onChange={(val) => setEditForm({ ...editForm, phone: val })}
              placeholder="Enter phone number"
              testId="phone"
              type="tel"
            />

            {/* Cricket Role - editable with dropdown */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="text-slate-600"><Target className="w-5 h-5" /></div>
              <div className="flex-1">
                <p className="text-xs text-slate-500">Cricket Role</p>
                {editing ? (
                  <Select
                    value={editForm.cricket_role}
                    onValueChange={(val) => setEditForm({ ...editForm, cricket_role: val })}
                  >
                    <SelectTrigger className="mt-1 h-9" data-testid="cricket-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {CRICKET_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-semibold text-[#0F172A]">{user?.cricket_role}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <p className="text-xs text-slate-600">Total Paid</p>
            </div>
            <p className="text-2xl font-bold text-green-600">₹{totalPaid.toFixed(0)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-slate-600" />
              <p className="text-xs text-slate-600">Total Due</p>
            </div>
            <p className="text-2xl font-bold text-slate-600">₹{totalDue.toFixed(0)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-green-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-green-600" />
              <p className="text-xs text-slate-600">Wallet</p>
            </div>
            <p className="text-2xl font-bold text-green-600">₹{balance.toFixed(0)}</p>
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4">Payment History</h2>
          {payments.length === 0 ? (
            <p className="text-slate-600 text-center py-8">No payment records</p>
          ) : (
            <div className="space-y-3">
              {payments.map((payment) => (
                <PaymentHistoryRow key={payment.payment_id} payment={payment} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex justify-around items-center z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <NavButton icon={<Calendar />} label="Matches" onClick={() => navigate('/dashboard')} />
        <NavButton icon={<User />} label="Profile" active onClick={() => {}} />
        <AddToHomeScreen />
        {user?.is_admin && (
          <NavButton icon={<ShieldAlert />} label="Admin" onClick={() => navigate('/admin')} />
        )}
      </div>
    </div>
  );
}

function NavButton({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
        active ? 'text-[#E63946]' : 'text-slate-600 hover:text-[#E63946]'
      }`}
      data-testid={`nav-${label.toLowerCase()}`}
    >
      <div className="w-6 h-6">{icon}</div>
      <span className="text-xs font-semibold">{label}</span>
    </button>
  );
}

function EditableField({ icon, label, value, editing, editValue, onChange, placeholder, testId, hint, type = 'text' }) {
  return (
    <>
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
        <div className="text-slate-600">{icon}</div>
        <div className="flex-1">
          <p className="text-xs text-slate-500">{label}</p>
          {editing ? (
            <Input
              value={editValue}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className="mt-1 h-9"
              type={type}
              data-testid={`${testId}-input`}
            />
          ) : (
            <p className="font-semibold text-[#0F172A]">{value}</p>
          )}
        </div>
      </div>
      {hint && !editing && (
        <p className="text-xs text-slate-500 -mt-2 ml-10">{hint}</p>
      )}
    </>
  );
}

function PaymentHistoryRow({ payment }) {
  return (
    <div 
      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg"
      data-testid={`payment-history-${payment.payment_id}`}
    >
      <div>
        <p className="font-semibold text-[#0F172A]">
          {payment.match?.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
        </p>
        <p className="text-sm text-slate-600">
          {payment.match?.date && format(new Date(payment.match.date), 'dd MMM yyyy')}
        </p>
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-[#0F172A]">₹{payment.amount.toFixed(0)}</p>
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
          payment.status === 'completed' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-slate-100 text-slate-800'
        }`}>
          {payment.status.toUpperCase()}
        </span>
      </div>
    </div>
  );
}

export default Profile;
